/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kSPRestRequestCore - RestRequestContext, the per-request state of kSPRestCore (success / error / retry and digest-refresh handling).
 * Split out of kSPRestCore.ts (2026-10-19, owner: smaller files for the reuse reviewer). kSPRestCore.ts
 * re-exports every public name, so importers of "./kSPRestCore" are unchanged.
 */

import { RestResponseError, RestRequestOptions } from "./kRestExecutor";
import { getCoreUtils, doCallBack, kNotNullOrUndefined } from "./kCoreHandler";
import { kRetryCore } from "./kRetryCore";
import type { kSPRestCore } from "./kSPRestCore";


// ── Per-request context ──────────────────────────────────────────────────────

export class RestRequestContext {
    private static readonly MaxRetries: number = 3;
    private static readonly BaseDelayMs: number = 500;

    /**
     * The REST client class this context reports to and retries through. kSPRestCore sets it once at its
     * module load (kSPRestCore.ts imports this module), so this module never imports its parent at runtime.
     */
    public static Rest: typeof kSPRestCore;

    public IgnoreError: boolean = false;

    /**
     * HTTP statuses this ONE caller considers EXPECTED — tolerated like `IgnoreError`, but for those
     * statuses only. Empty (the default) means the flag alone decides, so every existing caller is
     * byte-identical.
     *
     * WHY THIS EXISTS RATHER THAN `IgnoreError = true`. The constitution's own canonical fate-1
     * example is "an expected 404", and a caller that meets one had exactly two options before this:
     * report it (latching the gate over a condition that is not a fault) or tolerate EVERYTHING
     * (hiding the 403s and 500s that are). MOSE's stats and intake timers took the first and paid
     * for it — measured live 2026-08-18, one removed area whose row outlives its site by design
     * produced 14 reports in 8 minutes across two timers, each one latching the gate and silently
     * no-opping that cycle's work for every OTHER area.
     *
     * The alternative considered and rejected was for the caller to tolerate the transport and
     * re-report the unexpected statuses itself. That works, and it LOSES the diagnosis: the rich
     * report composed here carries the failing URL, the attempt summary and the call-site comment,
     * and a hand-rolled one at the call site carries none of them. Tolerating a NAMED status keeps
     * the full report for everything the caller did not name.
     */
    public TolerateStatuses: number[] = [];
    public ErrorHandler: ((error: RestResponseError) => void) | null = null;
    public Comments: string = "";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public Callback: ((data: any) => void) | null = null;
    public Options: RestRequestOptions = new RestRequestOptions();
    public RetryCount: number = 0;

    /**
     * When the FIRST retry for this request was scheduled; 0 when none ever was.
     *
     * DIAGNOSTIC ONLY — it decides nothing. It exists because on 2026-08-17 a reported Save Conflict
     * could not be told apart from the SAME conflict before the retry existed: the report carried the
     * body, the url and the comment, and nothing about whether anything had been attempted. Answering
     * "did the retry engage?" needed an Application Insights query, and the obvious unscoped form of
     * that query returns retry lines for OTHER urls and nearly refuted a working fix. A failure that
     * cannot say how hard it tried costs an investigation every time it happens.
     */
    private _FirstRetryMs: number = 0;

    /**
     * The failure a scheduled retry is retrying, kept so RetryRequest can RELEASE the caller if the
     * error gate has latched by the time the backoff elapses. Null when no retry is pending.
     *
     * Without it that path is a bare return: the retry never fires, nobody calls the ErrorHandler,
     * and the invocation waits for a callback that can no longer arrive. That window is seconds wide
     * by design (kRetryCore backoff), which is ample for a concurrent operation to latch the gate.
     */
    private _PendingError: RestResponseError | null = null;

    /**
     * ⚠ SETTLES, it does not just return — AND THIS IS THE SUCCESS PATH, WHICH IS THE WHOLE REASON
     * IT OUTLIVED THREE SEPARATE CORRECTIONS TO ITS SIBLINGS (fixed 2026-09-18).
     *
     * `OnError`, `_HandleRealError` and `RetryRequest` were each fixed for exactly this shape, and
     * every fixture written to prove those fixes drives a FAILURE — so not one of them ever called
     * this method at all. A request that SUCCEEDS after some OTHER operation has latched the gate
     * bare-returned here: the caller's Callback was never invoked, its ErrorHandler was never
     * invoked, and the invocation waited for an answer that had already arrived and been dropped on
     * the floor. Reproduced 2026-09-18 through the public `RestExecute` entry point with a 200 and a
     * real body: neither fate fired. Same family as `kMakePDFPromiseBridge` (LIVE-53) and
     * `kSPListItemsSaveLatch`; the same sentence applies — a bare return here is a hang, not a guard.
     *
     * ⚠ THE ERROR FATE IS TAKEN, NOT THE SUCCESS FATE, and the alternative was weighed rather than
     * assumed. Delivering the data would be `doCallBack(this.Callback, Data)` regardless of the
     * gate — one line, and it loses on two counts. It is CONSTITUTION LAW 1's named forbidden
     * costume ("a callback that settles as if the call succeeded"): the gate means a failure has
     * already been REPORTED in this invocation, so the caller would act on a genuine answer inside a
     * run already known to be broken — write the follow-up, mark the step complete. And it does not
     * even release reliably, because every house-style success callback opens with its own
     * `if (KErrorMode) { return; }`, so the delivery would be swallowed one frame down and whatever
     * THAT frame owed its own caller stays stranded. `_SettleUnsent` settled the same argument for
     * the never-sent case eight days earlier (LIVE-63): the ErrorHandler is this estate's RELEASE
     * path, and handing a success callback an answer it cannot distinguish from an authoritative one
     * "is the fail-open direction".
     *
     * ⚠ ONLY the ErrorHandler, never the success Callback — a caller that supplied no ErrorHandler
     * is left exactly as it was, which is unchanged behaviour for it. Same carve-out, same reason,
     * as `_SettleUnsent`.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public OnSuccess = (Data: any): void => {
        if (getCoreUtils().KErrorMode) { this._SettleUndelivered(); return; }
        try {
            doCallBack(this.Callback, Data);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext.OnSuccess"); }
    };

    /**
     * Release a caller whose request SUCCEEDED but whose answer cannot be delivered, because the
     * error gate latched while it was in flight.
     *
     * ⚠ StatusCode 0, deliberately, and it is the only honest value available. There IS no failing
     * HTTP status — the server answered — and putting the real 200 on a RestResponseError would hand
     * an error handler a success code to puzzle over. 0 already means "no HTTP status" everywhere in
     * this file, and `_IsToleratedStatus` refuses to tolerate 0, so a caller's `TolerateStatuses`
     * opt-in cannot swallow this by accident.
     *
     * The ResponseText says precisely what happened rather than borrowing a failure's wording: an
     * investigator reading "the request failed" about a request that returned 200 is being sent to
     * look at the wrong thing, and the ACTUAL fault is whatever latched the gate, which the central
     * handler has already reported in full.
     */
    // DOCUMENTED EXCEPTION: no KErrorMode guard, deliberately — this is reached BECAUSE the gate is
    // up, so a guard would refuse the one job it exists for and reinstate the stranding. The same
    // words, for the same reason, as `_SettleError` and `kSPRestCore._SettleUnsent` below.
    private _SettleUndelivered = (): void => {
        try {
            const Undelivered: RestResponseError = new RestResponseError();
            Undelivered.StatusCode = 0;
            Undelivered.Method = this.Options.Method;
            Undelivered.Url = this.Options.Url;
            Undelivered.ResponseText = "The request succeeded, but the error gate latched while it "
                + "was in flight, so its answer was not delivered. The fault is whatever raised the "
                + "gate and has already been reported. (" + this.Comments + ")";
            this._SettleError(Undelivered);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._SettleUndelivered");
        }
    };

    public OnError = (Error: RestResponseError): void => {
        // ⚠ SETTLES, it does not just return — this is the REACHABLE hang, and it strands BOTH fates.
        // A gate latched by some OTHER operation while this request was in flight used to make this a
        // bare return, so the caller's ErrorHandler was never called and the invocation waited for a
        // callback that could no longer arrive, burning the budget until the host killed it. That is
        // the failure MosePreservationProbe.OnRestError's header already names: "a bare return here is
        // a hang, not a guard". Releasing is safe by _SettleError's own contract — in error mode there
        // is nothing left to REPORT, but the caller must still be RELEASED.
        if (getCoreUtils().KErrorMode) { this._SettleError(Error); return; }
        try {
            // A status the caller NAMED as expected takes the ignored fate too — see TolerateStatuses.
            // Everything it did not name still gets the full report, which is the whole point of
            // naming one rather than reaching for IgnoreError.
            if (this.IgnoreError || this._IsToleratedStatus(Error.StatusCode) || this._IsOwnTimeout(Error)) {
                this._HandleIgnoredError(Error);
                return;
            }
            this._HandleRealError(Error);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext.OnError"); }
    };

    /**
     * A timed-out attempt of a caller that ASKED for the bound (Options.TimeoutMs > 0) and can hear a
     * failure (an ErrorHandler). It still goes through `_HandleIgnoredError`, so it is RETRIED first
     * (status 0 is transient); only the last attempt's timeout reaches the caller, un-reported - the
     * caller set the bound, so "no answer in time" is an outcome it has a fate for (SPArchive: the
     * library is shown unreadable with the reason). Without an ErrorHandler the ignored fate would be
     * `Callback(null)` - a fake success - so such a caller keeps the full report.
     */
    // DOCUMENTED EXCEPTION: a pure predicate over two values the caller already holds; it calls
    // nothing and cannot throw, and its false answer is the safe one - the failure REPORTS, as before.
    private _IsOwnTimeout = (Failure: RestResponseError): boolean => {
        return Failure.TimedOut === true && this.Options.TimeoutMs > 0 && kNotNullOrUndefined(this.ErrorHandler);
    };

    /**
     * SharePoint optimistic-concurrency conflict codes. A conflict is an EXPECTED condition on any
     * shared tenant — two writers touched the same item or file — so its correct fate is CORRECTED
     * (retry, law 1 fate 1), never REPORTED. Reporting latches KErrorMode, after which every guarded
     * write in the invocation silently no-ops.
     *
     * ⚠ -2130246326 WAS MISSING, AND ITS ABSENCE COST A RUN'S WHOLE RECORD (measured live, MOSE
     * 2026-08-17). SharePoint answered `Files/add(overwrite=true)` on a move-archive document, and a
     * list-item MERGE, with:
     *
     *     {"code":"-2130246326, Microsoft.SharePoint.SPException",
     *      "message":"Save Conflict\n\nYour changes conflict with those made concurrently ..."}
     *
     * Neither code below matched, so _TryRetry declined, _HandleRealError reported it, the gate
     * latched, and the guarded archive write plus every candidate-row write-back after it no-opped.
     * The run finished with its permanent governance record missing entirely. The retry that would
     * have absorbed it was already here and simply could not recognise the error.
     *
     * WHY THE CODES AND NOT THE SENTENCE: "Save Conflict" is user-facing prose and SharePoint
     * localises it, so a Swedish tenant would never match. These are SharePoint-AUTHORED HRESULTs —
     * facility 0x107 (0x8107054A) and 0x102 (0x81020037) — which is exactly the case where this
     * estate's rule says classify on the code. (The contrast is a facility-0x13 CLR code such as
     * -2146232832, which carries no information and must NOT be treated as a signal.)
     *
     * -2147467259 is bare E_FAIL (facility 0), kept for the item-busy variants that report it, and
     * it is the loosest of the three by some way — it is why this list is not narrowed further.
     */
    // DOCUMENTED EXCEPTION: a pure string predicate over a string the caller already holds. It calls
    // nothing and cannot throw, and its only honest failure direction is already the safe one — a
    // false answer means "not a conflict", which REPORTS rather than retries.
    private _IsConflict = (ResponseText: string): boolean => {
        return ResponseText.indexOf("-2147467259") > -1
            || ResponseText.indexOf("-2130575305") > -1
            || ResponseText.indexOf("-2130246326") > -1;
    };

    /**
     * A request URL the runtime could not even parse — PERMANENT, and never retried.
     *
     * `_IsTransientServerError` answers true for StatusCode 0, which is right for the failures that
     * genuinely are transient at that status: a timeout, a reset connection, a DNS blip. A URL that
     * cannot be parsed is none of those. It never reached a server, it will fail identically every
     * time, and the retry budget is spent for nothing — measured live 2026-08-18 on MOSE's
     * ExecuteMove, where a relative URL burned 4 attempts across 7.3s of a ~230s destructive window
     * before reporting a cause that named the parse failure rather than the missing host.
     *
     * WHY A MESSAGE MATCH IS ACCEPTABLE HERE, against this estate's usual rule: this string is
     * authored by the JavaScript RUNTIME (undici's `fetch`), not by a server. It is not a response
     * body, it carries no locale, and a tenant's language setting cannot change it — which is
     * exactly what makes SharePoint's own prose ("Save Conflict") unmatchable and its HRESULT the
     * right signal one method above.
     */
    // DOCUMENTED EXCEPTION: a pure string predicate over a string the caller already holds. It calls
    // nothing and cannot throw, and its only honest failure direction is the safe one — a false
    // answer leaves the failure retryable, which is the behaviour that shipped before this existed.
    private _IsUnparseableUrl = (ResponseText: string): boolean => {
        return ResponseText.indexOf("Failed to parse URL") > -1;
    };

    /**
     * Did THIS caller name this status as expected? See the `TolerateStatuses` field for why.
     *
     * A status of 0 can never be tolerated: it means the request never reached a server, so it is
     * not a status the caller could have formed an expectation about, and admitting it would let a
     * `[404]` opt-in quietly swallow every transport failure as well.
     */
    // DOCUMENTED EXCEPTION: a pure lookup over two values the caller already holds. It calls nothing
    // and cannot throw, and its false answer is the safe one — the failure REPORTS, as it does today.
    private _IsToleratedStatus = (StatusCode: number): boolean => {
        if (StatusCode <= 0) { return false; }
        return this.TolerateStatuses.indexOf(StatusCode) > -1;
    };

    /**
     * Stale form-digest detection. SharePoint returns 403 plus an error message that contains
     * "security validation" (or specific HRESULTs) when the page's X-RequestDigest has expired.
     * The user-visible symptom is a write failing on the first try and succeeding after a
     * page refresh — exactly the "must try 2-3 times" pattern reported 2026-04-22.
     */
    private _IsDigestExpired = (RestError: RestResponseError): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (RestError.StatusCode !== 403) { return false; }
            const Text: string = RestError.ResponseText;
            if (Text.indexOf("-2130575251") > -1) { return true; }
            // "... is invalid and might be corrupted": the same stale digest (the browser executor used to RELOAD the
            // page on it, silently - DocumentBuilder PX-113, 2026-10-01); a fresh digest and one more try, else reported
            if (Text.indexOf("-2130575252") > -1) { return true; }
            if (Text.indexOf("security validation") > -1) { return true; }
            if (Text.indexOf("X-Forms_Based_Authentication_Required") > -1) { return true; }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._IsDigestExpired");
            return false;
        }
    };

    /**
     * Transient server errors that are safe to retry without changing anything else
     * (gateway timeouts, server errors, throttling). Retrying does not assume a particular
     * cause — it just rides out brief blips that are common on shared SharePoint Online.
     */
    private _IsTransientServerError = (StatusCode: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (StatusCode === 0) { return true; }
            if (StatusCode === 408) { return true; }
            if (StatusCode === 429) { return true; }
            if (StatusCode >= 500 && StatusCode <= 599) { return true; }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._IsTransientServerError");
            return false;
        }
    };

    private _TryRetry = (RestError: RestResponseError): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (this.Options.NoRetry === true) { return false; }
            if (this.RetryCount >= RestRequestContext.MaxRetries) { return false; }
            // 412 Precondition Failed is the DEFINITIVE answer to an If-Match write: the row changed
            // under the caller, and re-sending the same ETag can only fail again. Its body carries the
            // same -2130575305 HRESULT as a Save Conflict, so without this line the conflict matcher
            // below would burn seconds of backoff on an answer that is already final (2026-08-22).
            if (RestError.StatusCode === 412) { return false; }
            if (this._IsDigestExpired(RestError)) { return this._ScheduleDigestRefreshRetry(RestError); }
            if (this._IsConflict(RestError.ResponseText)) { return this._ScheduleSimpleRetry("Save Conflict", RestError); }
            // BEFORE the status check, and that ORDER is the whole fix: an unparseable URL carries
            // StatusCode 0, which _IsTransientServerError answers true for.
            if (this._IsUnparseableUrl(RestError.ResponseText)) { return false; }
            if (this._IsTransientServerError(RestError.StatusCode)) {
                return this._ScheduleSimpleRetry("Transient HTTP " + RestError.StatusCode, RestError);
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._TryRetry");
            return false;
        }
    };

    private _ScheduleSimpleRetry = (Reason: string, RestError: RestResponseError): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            this.RetryCount++;
            if (this._FirstRetryMs === 0) { this._FirstRetryMs = Date.now(); }
            this._PendingError = RestError;
            // Reuse the shared retry-delay (law 2): honours a Retry-After the server sent (SharePoint /
            // Graph 429/503 throttles, capped at 120s by kRetryCore) else exponential backoff + jitter.
            // Replaces the bespoke BaseDelayMs * 2^n so a real throttle waits the server-directed time
            // instead of a fixed ~500ms that retries straight back into the same throttle and exhausts
            // MaxRetries (the 2026-07-11 SharePointStateService 429 incident; Improvements §01.13).
            const DelayMs: number = kRetryCore.GetRetryDelay(RestError, this.RetryCount - 1);
            console.warn("kSPRestCore: " + Reason + " on " + this.Options.Url
                + " — retry " + this.RetryCount + "/" + RestRequestContext.MaxRetries
                + " in " + DelayMs + "ms (" + this.Comments + ")");
            if (RestError.StatusCode === 429 || RestError.StatusCode === 503) {
                RestRequestContext.Rest.NotifyThrottled(RestError.StatusCode);
            }
            setTimeout(this.RetryRequest, DelayMs);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._ScheduleSimpleRetry");
            return false;
        }
    };

    /**
     * Schedules a retry that first refreshes the form digest from /_api/contextinfo on the
     * current site, then resubmits the original request with the fresh X-RequestDigest header.
     * Reuses Executor.FetchExternalDigest with the current site URL — same endpoint shape, just
     * pointed at the local site instead of a cross-site target.
     *
     * ⚠ IT TAKES THE FAILURE AND STORES IT, AND UNTIL 2026-09-20 IT DID NEITHER (fixed here).
     * `_ScheduleSimpleRetry` has always set `_PendingError`, because that is the failure
     * `_SettleRetry` releases the caller with if the gate latches during the backoff. This sibling
     * scheduled a retry and stored nothing, so the whole digest branch had nothing to settle WITH:
     * every downstream release found a null `_PendingError` and, by that method's own documented
     * contract, correctly did nothing. **Fixing the bare returns below without this line would have
     * looked like a fix and changed no behaviour at all** — when a release path is guarded by state,
     * check the state is ever SET before trusting a fix to the guard.
     */
    private _ScheduleDigestRefreshRetry = (RestError: RestResponseError): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            this.RetryCount++;
            if (this._FirstRetryMs === 0) { this._FirstRetryMs = Date.now(); }
            this._PendingError = RestError;
            const DelayMs: number = RestRequestContext.BaseDelayMs * Math.pow(2, this.RetryCount - 1);
            console.warn("kSPRestCore: Stale form digest on " + this.Options.Url
                + " — refreshing and retrying " + this.RetryCount + "/" + RestRequestContext.MaxRetries
                + " in " + DelayMs + "ms (" + this.Comments + ")");
            setTimeout(this._FetchFreshDigestThenRetry, DelayMs);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._ScheduleDigestRefreshRetry");
            return false;
        }
    };

    /**
     * ⚠ BOTH GUARDS SETTLE, they do not just return (fixed 2026-09-20). This is the digest-flavoured
     * twin of `RetryRequest`, three methods below, which was corrected for exactly this shape and
     * settles on both of its guards — and this copy kept the defect because every fixture written to
     * prove that fix drove the SIMPLE retry path and none of them ever produced a stale digest.
     *
     * The window is the full backoff — seconds — which is ample for a concurrent operation to latch
     * the gate or for a host to tear the executor down. A bare return then abandoned a retry nobody
     * would ever settle: no Callback, no ErrorHandler, the invocation waiting for ever.
     */
    private _FetchFreshDigestThenRetry = (): void => {
        if (getCoreUtils().KErrorMode) {
            this._SettleDigest("the error gate latched before the form digest could be refreshed");
            return;
        }
        try {
            if (!RestRequestContext.Rest._EnsureExecutorReady("RestRequestContext._FetchFreshDigestThenRetry")) {
                // _EnsureExecutorReady has already REPORTED the missing executor, so there is
                // nothing left to report here — only the caller left to release.
                this._SettleDigest("the executor was gone before the form digest could be refreshed");
                return;
            }
            // The digest of the web the request WENT TO (it used to be the current site's, wrong for
            // every cross-web POST), and a FRESH one: the browser executor caches digests per web
            // (2026-09-24), and without the invalidation the retry was handed back the digest the
            // server had just refused.
            const WebUrl: string = RestRequestContext.WebOfRequest(this.Options.Url, RestRequestContext.Rest.URLs.CurrSiteFullURL);
            RestRequestContext.Rest.Executor.InvalidateExternalDigest(WebUrl);
            RestRequestContext.Rest.Executor.FetchExternalDigest(WebUrl, this.OnExternalDigestReceived);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._FetchFreshDigestThenRetry");
        }
    };

    /** "https://t/sites/A/sub/_api/web/..." -> "https://t/sites/A/sub"; a RELATIVE url (a call to the
        current site) or one without "/_api/" -> `Fallback`. Pure. */
    public static WebOfRequest = (Url: string, Fallback: string): string => {
        if (getCoreUtils().KErrorMode) { return Fallback; }
        try {
            const At: number = Url.toLowerCase().indexOf("/_api/");
            if (At <= 0 || Url.toLowerCase().indexOf("http") !== 0) { return Fallback; }
            return Url.substring(0, At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext.WebOfRequest");
            return Fallback;
        }
    };

    private _HandleIgnoredError = (RestError: RestResponseError): void => {
        // Settles rather than returning bare, matching its _HandleRealError sibling.
        // ⚠ HONESTLY: this one CANNOT FIRE TODAY — OnError checks the same gate first and settles, so
        // nothing reaches here with it raised. It is kept for symmetry between the two fates, and is
        // written down as unreachable rather than dressed up as the fix; the reachable strandings were
        // OnError and RetryRequest. A settle is idempotent in practice only because whichever guard
        // fires first RETURNS, so the ErrorHandler is still called exactly once.
        if (getCoreUtils().KErrorMode) { this._SettleError(RestError); return; }
        try {
            if (this._TryRetry(RestError)) { return; }
            if (kNotNullOrUndefined(this.ErrorHandler)) {
                doCallBack(this.ErrorHandler, RestError);
                return;
            }
            doCallBack(this.Callback, null);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._HandleIgnoredError");
        }
    };

    /**
     * A REAL (non-ignored) failure: REPORT it centrally, then SETTLE the caller if it supplied an
     * ErrorHandler.
     *
     * The settle was missing, and its absence is a hang factory. On this path the caller's success
     * callback is never invoked and — until now — neither was its ErrorHandler, so any caller waiting
     * on one of the two simply waited forever. In a browser that looks like a spinner that never
     * stops; inside an Azure Function it strands the whole invocation until the host's timeout kills
     * it minutes later with a generic 500 and nothing useful logged. LIVE-CONFIRMED 2026-07-27: a
     * MOSE move that had already copied, verified, deleted and signposted a file disappeared at its
     * audit write and burned the full 5-minute function timeout.
     *
     * Settling does NOT weaken reporting — KGlobalErrorHandler has already run and latched
     * KErrorMode, so the resumed caller's own guard short-circuits and it stops promptly with a real
     * reason instead of a timeout. A reported error plus a clean stop beats a reported error plus a
     * hang. Callers that pass no ErrorHandler are completely unaffected.
     */
    /**
     * One line for the report saying how hard this request tried before it gave up.
     *
     * It states BOTH outcomes explicitly, because the two are the ones an investigator has to tell
     * apart and silence looks identical for each: "not retryable" (no matcher recognised the failure —
     * the shape that hid the -2130246326 Save Conflict for a year) versus "retried and the budget ran
     * out" (a genuine contention, where the remedy is concurrency, not a matcher).
     *
     * NO KErrorMode GUARD, deliberately: the only caller has already established the gate is DOWN and
     * returns early otherwise, so a guard here could never fire — and a guard that cannot fire is a
     * false claim about what has been checked.
     */
    // DOCUMENTED EXCEPTION: the reason is the paragraph directly above — the guard could never fire
    // here, and a guard that cannot fire is a false claim about what has been checked. The marker
    // exists because the checker reads markers, not prose: "silence is never an exemption", and a
    // justification it cannot see is exactly that silence.
    private _AttemptSummary = (): string => {
        try {
            if (this.RetryCount === 0) {
                return "Attempts: 1 — NOT retried: this failure matched no retryable shape.";
            }
            let Elapsed: string = "";
            if (this._FirstRetryMs > 0) {
                Elapsed = " spanning " + String(Date.now() - this._FirstRetryMs) + "ms";
            }
            return "Attempts: " + String(this.RetryCount + 1) + " (1 initial + "
                + String(this.RetryCount) + " retries" + Elapsed + ") — retryable, but the budget of "
                + String(RestRequestContext.MaxRetries) + " was exhausted and it still failed.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._AttemptSummary");
            return "";
        }
    };

    // DOCUMENTED EXCEPTION (try-catch-wrap): the REPORT is wrapped; the `_SettleError` call after it
    // is deliberately OUTSIDE that try, so the caller is released even when composing or reporting
    // the diagnosis throws. Pulling it inside would make a failure in the MESSAGE strand the request,
    // which is the hang this class has already had to fix twice.
    private _HandleRealError = (RestError: RestResponseError): void => {
        if (getCoreUtils().KErrorMode) { this._SettleError(RestError); return; }
        try {
            if (this._TryRetry(RestError)) { return; }
            const ErrObj: Error = new Error(
                RestError.ResponseText +
                "\nRestCall: " + this.Options.Url +
                "\n" + this._AttemptSummary() +
                "\n" + this.Comments
            );
            let StatusLabel: string = "Error";
            if (RestError.StatusCode > 0) { StatusLabel = String(RestError.StatusCode); }
            ErrObj.name = "REST " + StatusLabel;
            getCoreUtils().KGlobalErrorHandler(ErrObj, "kSPRestCore.RestExecute");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._HandleRealError");
        }
        this._SettleError(RestError);
    };

    /**
     * Release the caller of a retry that can no longer be issued, using the failure it was retrying.
     *
     * NO KErrorMode GUARD, deliberately: both call sites are reached precisely BECAUSE the gate is up
     * or the transport is gone, so a guard here would refuse to do the one job it exists for — which
     * is the bare-return hang wearing a different shape.
     *
     * A null _PendingError means no retry was ever scheduled, so there is nobody waiting on one and
     * settling would invent a failure. That case returns without calling anything.
     */
    // DOCUMENTED EXCEPTION: see the paragraph above — this is reached BECAUSE the gate is up, so a
    // KErrorMode guard would refuse the one job it exists for and reinstate the hang.
    private _SettleRetry = (): void => {
        try {
            if (this._PendingError === null) { return; }
            const Pending: RestResponseError = this._PendingError;
            this._PendingError = null;
            this._SettleError(Pending);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._SettleRetry");
        }
    };

    /**
     * Release the caller of a request that was waiting on a form digest it can no longer use.
     *
     * ⚠ TWO ENTRY PATHS REACH THE DIGEST CALLBACK AND THEY OWE THE CALLER DIFFERENT THINGS, which
     * is why `_SettleRetry` alone is not the answer here. `OnExternalDigestReceived` is reached from
     * a scheduled RETRY — where `_PendingError` holds the failure being retried and releasing with
     * it is exactly right — and ALSO directly from `RestExecuteExternal`'s non-GET branch, where no
     * retry was ever scheduled, `_PendingError` is null, and the caller is waiting on a FIRST
     * attempt that was never sent. `_SettleRetry`'s documented contract is to return when
     * `_PendingError` is null because "settling would invent a failure" — true for `RetryRequest`,
     * which is only ever reached from a scheduled retry, and silently wrong for that second path.
     * Using it there would have released nobody while looking like a fix.
     *
     * The never-sent wording matches `kSPRestCore._SettleUnsent`, which settled the same argument
     * for the same shape (LIVE-63): StatusCode 0 because there IS no failing HTTP status — nothing
     * was ever sent — and `_IsToleratedStatus` refuses to tolerate 0, so a caller's
     * `TolerateStatuses` opt-in cannot swallow a stranding by accident.
     */
    // DOCUMENTED EXCEPTION: no KErrorMode guard, deliberately — every call site is reached BECAUSE
    // the gate is up or the transport is gone, so a guard would refuse the one job it exists for
    // and reinstate the stranding. Same words, same reason, as `_SettleRetry` and `_SettleError`.
    private _SettleDigest = (Reason: string): void => {
        try {
            if (this._PendingError !== null) { this._SettleRetry(); return; }
            const Unsent: RestResponseError = new RestResponseError();
            Unsent.StatusCode = 0;
            Unsent.Method = this.Options.Method;
            Unsent.Url = this.Options.Url;
            Unsent.ResponseText = Reason + " (" + this.Comments + ")";
            this._SettleError(Unsent);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._SettleDigest");
        }
    };

    /** Release a caller that supplied an ErrorHandler. Deliberately outside the guard above: in error
     *  mode there is nothing left to report, but the caller must still be released. */
    // DOCUMENTED EXCEPTION: stated in the line above — "deliberately outside the guard". In error
    // mode there is nothing left to REPORT, but the caller must still be RELEASED, and a guard here
    // would strand it.
    private _SettleError = (RestError: RestResponseError): void => {
        try {
            if (kNotNullOrUndefined(this.ErrorHandler)) { doCallBack(this.ErrorHandler, RestError); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext._SettleError");
        }
    };

    public RetryRequest = (): void => {
        // ⚠ THE SECOND REACHABLE STRANDING, and the widest window of the three: this fires after a
        // kRetryCore backoff, so SECONDS pass between scheduling and arriving here — ample for another
        // operation to latch the gate. A bare return then abandoned a retry nobody would ever settle.
        if (getCoreUtils().KErrorMode) { this._SettleRetry(); return; }
        try {
            if (!RestRequestContext.Rest._EnsureExecutorReady("RestRequestContext.RetryRequest")) {
                // Same reasoning: the executor is gone, so the retry cannot be issued. Releasing the
                // caller with the failure it was already retrying beats waiting for a request that
                // will never be sent. _EnsureExecutorReady has already reported the missing executor.
                this._SettleRetry();
                return;
            }
            RestRequestContext.Rest.Executor.ExecuteRequest(
                this.Options, this.OnSuccess, this.OnError
            );
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext.RetryRequest");
        }
    };

    /**
     * ⚠ BOTH GUARDS SETTLE, they do not just return (fixed 2026-09-20), and this is the method with
     * TWO callers that owe the caller different things — see `_SettleDigest`, which is why a new
     * helper exists rather than a second call to `_SettleRetry`.
     *
     * It is reached from `_FetchFreshDigestThenRetry` (a scheduled retry, so `_PendingError` holds
     * the failure being retried) and from `RestExecuteExternal`'s non-GET branch (no retry, nothing
     * pending, a first attempt that was never sent). `RestExecuteExternal`'s own two refusals were
     * corrected for the stranding on 2026-09-10 and settle through `_SettleUnsent`; **this third
     * exit from the same method, one asynchronous hop later, was missed** — the fix went to the
     * guards that were in the same file view and not to the callback they hand off to.
     */
    public OnExternalDigestReceived = (Digest: string): void => {
        if (getCoreUtils().KErrorMode) {
            this._SettleDigest("the error gate latched while the form digest was in flight, so the "
                + "request was never sent");
            return;
        }
        try {
            if (!RestRequestContext.Rest._EnsureExecutorReady("RestRequestContext.OnExternalDigestReceived")) {
                // Already reported by _EnsureExecutorReady; only the caller is left to release.
                this._SettleDigest("the executor was gone when the form digest arrived, so the "
                    + "request was never sent");
                return;
            }
            this.Options.Headers["X-RequestDigest"] = Digest;
            RestRequestContext.Rest.Executor.ExecuteRequest(
                this.Options, this.OnSuccess, this.OnError
            );
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "RestRequestContext.OnExternalDigestReceived");
        }
    };
}
